import debug from "debug";

const log = debug("app:fn");

/**
 * Class for creating a draggable and resizable modal.
 * @category Components
 * @subcategory ContextMenu
 */
export class DragResizeModal {
  /**
   * Initializes a draggable and resizable modal.
   * @param {string|HTMLElement} handleSelector - The selector or element for the drag handle.
   * @param {HTMLElement} element - The modal element to make draggable and resizable.
   */
  constructor(handleSelector, element) {
    this.element = element;

    // If handleSelector is a string, use it to find the handle, otherwise assign directly
    this.handle =
      typeof handleSelector === "string"
        ? element.querySelector(handleSelector)
        : handleSelector;

    if (!this.handle) {
      console.error("Drag handle not found or provided correctly.");
      return; // Stop further initialization if handle is not found
    }

    this.initDraggable();
    // this.initResizable();
  }


  cleanup() {
    console.log("DragResizeModal cleanup started...");

  
    // 1. Remove drag event listeners
    if (this.handle) {
      this.handle.removeEventListener("mousedown", this.onDragStart);
    }
    document.removeEventListener("mousemove", this.onMouseMove);
    document.removeEventListener("mouseup", this.onMouseUp);
  
    // 2. Remove resize event listeners
    if (this.resizer) {
      this.resizer.removeEventListener("mousedown", this.resizeStart);
    }
    document.removeEventListener("mousemove", this.resize);
    document.removeEventListener("mouseup", this.stopResize);
  
    // 3. Clear object references
    this.handle = null;
    this.element = null;
    this.resizer = null;
  
    // 4. Reset global styles
    document.body.style.cursor = "default";
  
    console.log("DragResizeModal cleanup completed.");
  }

  /**
   * Initializes the draggable functionality for the modal.
   * Enables the modal to be dragged around the screen by the handle.
   */
  initDraggable() {
    let isDragging = false;
    let pointerStartX, pointerStartY, modalStartX, modalStartY;

    // Set initial cursor to 'grab'
    this.handle.style.cursor = "grab";

    // Mouse down event starts dragging
    this.handle.addEventListener("mousedown", (e) => {
      e.preventDefault();
      isDragging = true;

      // Change cursor to 'grabbing'
      this.handle.style.cursor = "grabbing";
      document.body.style.cursor = "grabbing";

      // Record initial mouse position and modal position
      const rect = this.element.getBoundingClientRect();
      pointerStartX = e.clientX;
      pointerStartY = e.clientY;
      modalStartX = rect.left;
      modalStartY = rect.top;

      // If the modal is centered with transform (e.g. translate(-50%, -50%)),
      // switch to left/top positioning so drag delta matches the visual position.
      const computed = window.getComputedStyle(this.element);
      if (computed.transform && computed.transform !== "none") {
        this.element.style.transform = "none";
        this.element.style.left = `${rect.left}px`;
        this.element.style.top = `${rect.top}px`;
      }

      // Add event listeners for mousemove and mouseup
      document.addEventListener("mousemove", onMouseMove);
      document.addEventListener("mouseup", onMouseUp);
    });

    // Mouse move event for updating modal position
    const onMouseMove = (e) => {
      if (!isDragging) return;

      // Calculate new position based on mouse delta
      const deltaX = e.clientX - pointerStartX;
      const deltaY = e.clientY - pointerStartY;

      let newLeft = modalStartX + deltaX;
      let newTop = modalStartY + deltaY;

      // Restrict dragging underneath the header for BOM table and Power table
      if (this.element.id === "bomTable-modal" || this.element.id === "powerTable-modal") {
        const header = document.getElementById("catHeader");
        if (header) {
          const headerRect = header.getBoundingClientRect();
          const minTop = headerRect.bottom;
          // Ensure the modal's top edge doesn't go below the header's bottom edge
          if (newTop < minTop) {
            newTop = minTop;
          }
        }
      }

      this.element.style.left = `${newLeft}px`;
      this.element.style.top = `${newTop}px`;
    };

    // Mouse up event ends dragging
    const onMouseUp = () => {
      isDragging = false;

      // Reset cursor
      this.handle.style.cursor = "grab";
      document.body.style.cursor = "default";

      // Clean up event listeners
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    };
  }

  /**
   * Initializes the resizable functionality for the modal.
   * Enables the modal to be resized using a resizer element.
   */
  initResizable() {
    let resizer = this.element.querySelector(".resizer");

    // Recreate the resizer if it doesn't exist
    if (!resizer) {
      resizer = document.createElement("div");
      resizer.className = "resizer";
      this.element.appendChild(resizer);
    }

    /**
     * Handles the resizing of the modal during mouse movement.
     * @param {MouseEvent} event - The mouse event containing position data.
     */
    const resize = (event) => {
      this.element.style.width = `${event.clientX - this.element.offsetLeft}px`;
      this.element.style.height = `${event.clientY - this.element.offsetTop}px`;
      log("Resizing active", {
        width: this.element.style.width,
        height: this.element.style.height,
      });
    };

    /**
     * Stops the resizing process and removes event listeners.
     */
    const stopResize = () => {
      document.removeEventListener("mousemove", resize);
      document.removeEventListener("mouseup", stopResize);
    };

    // Ensure only one mousedown event listener is attached
    resizer.removeEventListener("mousedown", this.resizeStart);
    resizer.addEventListener(
      "mousedown",
      (this.resizeStart = (event) => {
        log("Resizer activated", event); // Log here
        event.preventDefault();
        document.addEventListener("mousemove", resize);
        document.addEventListener("mouseup", stopResize);
      })
    );
  }
}
